import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

interface Policy {
  id: string
  name: string
  status: string
}

interface BulkDeleteDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  policies: Policy[]
  onConfirm: () => void
  isLoading?: boolean
}

/**
 * BulkDeleteDialog confirms deletion of multiple policies
 *
 * Features:
 * - Shows count of policies to delete
 * - Warning about ACTIVE policies being excluded
 * - Lists first 5 policy names
 * - Cancel/Delete buttons with loading state
 */
export function BulkDeleteDialog({
  open,
  onOpenChange,
  policies,
  onConfirm,
  isLoading = false,
}: BulkDeleteDialogProps) {
  const activePolicies = policies.filter((p) => p.status === 'ACTIVE')
  const deletablePolicies = policies.filter((p) => p.status !== 'ACTIVE')
  const displayLimit = 5
  const hasMore = deletablePolicies.length > displayLimit

  const handleConfirm = () => {
    onConfirm()
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="bulk-delete-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Multiple Policies</AlertDialogTitle>
          <AlertDialogDescription>
            You are about to delete{' '}
            <span className="font-semibold text-foreground">
              {deletablePolicies.length} {deletablePolicies.length === 1 ? 'policy' : 'policies'}
            </span>
            .
          </AlertDialogDescription>
        </AlertDialogHeader>

        {activePolicies.length > 0 && (
          <div className="rounded-md bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-400">
            <strong>Note:</strong> {activePolicies.length} active{' '}
            {activePolicies.length === 1 ? 'policy' : 'policies'} will be
            excluded. Active policies must be undeployed before deletion.
          </div>
        )}

        {deletablePolicies.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              The following policies will be deleted:
            </p>
            <ul className="list-inside list-disc space-y-1 text-sm">
              {deletablePolicies.slice(0, displayLimit).map((policy) => (
                <li key={policy.id} className="text-foreground">
                  {policy.name}
                </li>
              ))}
              {hasMore && (
                <li className="text-muted-foreground">
                  ...and {deletablePolicies.length - displayLimit} more
                </li>
              )}
            </ul>
          </div>
        )}

        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          This action cannot be undone. All selected policies and their
          associated data will be permanently removed.
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isLoading || deletablePolicies.length === 0}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            data-testid="bulk-delete-confirm-button"
          >
            {isLoading
              ? 'Deleting...'
              : `Delete ${deletablePolicies.length} ${deletablePolicies.length === 1 ? 'Policy' : 'Policies'}`}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
